import {
  parseJsonEventStream,
  readUIMessageStream,
  uiMessageChunkSchema,
  type UIMessage,
  type UIMessageChunk,
} from "ai";

function parseUiChunkStream(body: ReadableStream<Uint8Array>): ReadableStream<UIMessageChunk> {
  return parseJsonEventStream({
    stream: body,
    schema: uiMessageChunkSchema,
  }).pipeThrough(
    new TransformStream({
      transform(chunk, controller) {
        if (chunk.success) {
          controller.enqueue(chunk.value);
        } else {
          throw chunk.error;
        }
      },
    })
  );
}

function readableStreamFromChunks(chunks: UIMessageChunk[]): ReadableStream<UIMessageChunk> {
  let index = 0;
  return new ReadableStream({
    pull(controller) {
      if (index >= chunks.length) {
        controller.close();
        return;
      }
      controller.enqueue(chunks[index]!);
      index += 1;
    },
  });
}

export type MergeUiMessageStreamOptions = {
  onStreamPart?: (part: UIMessageChunk) => void;
};

/**
 * Merge an AI SDK UI message stream (e.g. from approve/decline) into the chat message list.
 */
export async function mergeUiMessageStream(
  response: Response,
  priorMessages: UIMessage[],
  setMessages: (messages: UIMessage[]) => void,
  options?: MergeUiMessageStreamOptions
): Promise<UIMessage[]> {
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Request failed (${response.status})`);
  }
  if (!response.body) {
    throw new Error("Empty response body");
  }

  const last = priorMessages[priorMessages.length - 1];
  const seed: UIMessage =
    last?.role === "assistant"
      ? last
      : {
          id: crypto.randomUUID(),
          role: "assistant",
          parts: [],
        };

  const bufferedChunks: UIMessageChunk[] = [];
  const source = parseUiChunkStream(response.body);
  const reader = source.getReader();
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    options?.onStreamPart?.(value);
    bufferedChunks.push(value);
  }

  const stream = readableStreamFromChunks(bufferedChunks);
  let finalMessage = seed;
  for await (const msg of readUIMessageStream({ message: seed, stream })) {
    finalMessage = msg;
  }

  const nextMessages =
    last?.role === "assistant" && last.id === seed.id
      ? [...priorMessages.slice(0, -1), finalMessage]
      : [...priorMessages, finalMessage];
  setMessages(nextMessages);
  return nextMessages;
}
